import {
  FULL_QUILL_FORMATS,
  FULL_QUILL_MODULES,
} from "../../shared/customRichTextEditor";
import { PARAMS } from '../../../constants/pages';
import { PEOPLE_DEFAULT_ORDERING } from '../../../constants/people';
import { SYSTEM_ROLES } from '../../../constants/roles';
import { USER_STATUSES } from '../../../constants/statuses';
import {
  ABSENCE_REASON_FIELD,
  ABSENCE_SET_FIELD_NAMES,
  ABSENCE_FIELDS,
  ABSENCE_FIELD_NAMES,
  ABSENCE_COLORS,
  ABSENCE_ICONS,
} from '../../../constants/absence';
import {
  api_existing_holiday_dates,
  API_UNIQUE_ABSENCE_SET,
  API_USERS_BASIC,
} from '../../../constants/apiRoutes';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { prepareReasons, prepareRolloverDate } from '../../../utility/absence';
import http from '../../../utility/http';
import {
  isEmpty,
  forbiddenCharacters,
  validateLength,
  validateRequiredMultipleDates,
  validateUniqueMultipleDates,
  validateMultipleField,
} from '../../../utility/validation';
import { getStartOfYear } from '../../../utility/dateUtils';
import {
  asyncDebounce,
  getObjectToNumberArray,
  isArrayEmpty,
  trimString,
  getItemById,
} from '../../../utility/helpers';
import {
  getUserStatusLabel,
  parseDuplicateParameters,
  getRandomColorFromColorArray,
} from '../../../utility/uiUtils';

const {
  ABSENCE_SET,
  ID,
  ROLE,
  STATUS,
  SCOPE,
  JOB_TITLE,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  TAGS,
  EXCLUDE_ROLE,
  EXCLUDE_STATUS,
} = PARAMS;

const {
  ABSENCE_SET_ID,
  NAME,
  ORDER,
  ICON,
  COLOR,
  HAS_DAYS_ENABLED,
  NUMBER_OF_DAYS,
  ACCRUAL,
  HAS_ROLLOVER,
  ROLLOVER_DATE,
  HAS_EXTENSIONS,
  EXTENSIONS,
  HAS_TEMPLATE,
  HAS_REASONS,
  REASONS,
  USER_COUNT,
  TEMPLATE,
} = ABSENCE_FIELD_NAMES;

export const DUPLICATE_SET_FIELD = [
  {
    name: ABSENCE_SET_FIELD_NAMES.NAME,
    type: FIELD_TYPES.TEXT,
    required: true,
    shouldValidateInitialValue: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
      {
        type: 'availableName',
        validator: asyncDebounce(absenceSet => {
          const params = {
            name: trimString(absenceSet),
          };
          return http
            .get(API_UNIQUE_ABSENCE_SET, { params })
            .then(({ data }) => !data);
        }, 300),
        async: true,
      },
    ],
  },
];

export const prepareAbsence = (absence, absenceSet) => ({
  ...(absence.id ? { id: absence.id } : {}),
  [ORDER]: absence.id ? absence[ORDER] : absenceSet.types.length,
  [NAME]: absence[NAME],
  [ICON]: absence[ICON],
  [COLOR]: absence[COLOR],
  [HAS_DAYS_ENABLED]: absence[HAS_DAYS_ENABLED],
  [NUMBER_OF_DAYS]: absence[NUMBER_OF_DAYS],
  [ACCRUAL]: absence[ACCRUAL],
  [HAS_ROLLOVER]: absence[HAS_ROLLOVER],
  [ROLLOVER_DATE]: absence[ROLLOVER_DATE],
  [HAS_EXTENSIONS]: absence[HAS_EXTENSIONS],
  [HAS_TEMPLATE]: absence[HAS_TEMPLATE],
  [HAS_REASONS]: absence[HAS_REASONS],
  [REASONS]: absence[REASONS].reduce(
    (acc, reason) => [...acc, reason.name],
    []
  ),
  [ABSENCE_SET_ID]: absenceSet.id,
});

export const prepareAbsences = absences => {
  return absences.reduce(
    (acc, absence, index) => [
      ...acc,
      {
        ...(absence.id ? { id: absence.id } : {}),
        [ORDER]: index + 1,
        [NAME]: absence[NAME],
        [ICON]: absence[ICON],
        [COLOR]: absence[COLOR],
        [HAS_DAYS_ENABLED]: absence[HAS_DAYS_ENABLED],
        [NUMBER_OF_DAYS]: absence[NUMBER_OF_DAYS],
        [ACCRUAL]: absence[ACCRUAL],
        [HAS_ROLLOVER]: absence[HAS_ROLLOVER],
        [ROLLOVER_DATE]: absence[ROLLOVER_DATE],
        [HAS_EXTENSIONS]: absence[HAS_EXTENSIONS],
        [HAS_TEMPLATE]: absence[HAS_TEMPLATE],
        [HAS_REASONS]: absence[HAS_REASONS],
        [REASONS]: absence[REASONS],
      },
    ],
    []
  );
};

export const prepareUsersField = (classes, translations) => {
  return [
    {
      name: 'users',
      type: FIELD_TYPES.MANAGE_USERS_LIST,
      translationKey: 'users',
      className: classes.picker,
      fieldWrapperClass: classes.field,
      hasDelete: true,
      shouldRemoveFieldSpacing: true,
      showSelection: true,
      shouldCalculateSelectionHeight: true,
      shouldHandleTypeLabel: true,
      handleTypeLabel: user =>
        getUserStatusLabel(translations.absenceSets.deactivated, user),
    },
  ];
};

export const prepareRemoveUsersInitialData = users => ({
  users,
});

export const prepareAddUserList = (allUsers, assignedUsers = []) =>
  allUsers.reduce((acc, user) => {
    const assignedUser = getItemById(assignedUsers, user.id);
    const isDeactivated = user.status === USER_STATUSES.DEACTIVATED;

    return assignedUser || isDeactivated ? acc : [...acc, user];
  }, []);

export const prepareAddAbsenceOrExtensionUsersInitialData = (
  allUsers = []
) => ({
  users: {
    selected: {
      [ID]: [],
      [ROLE]: [],
      [STATUS]: [],
      [SCOPE]: [],
      [JOB_TITLE]: [],
      [TRACK]: [],
      [LEVEL]: [],
      [DIVISION]: [],
      [DEPARTMENT]: [],
      [TAGS]: [],
    },
    filteredValues: allUsers,
  },
});

export const prepareAddUsersInitialData = (allUsers, types = []) => ({
  users: {
    selected: {
      [ID]: [],
      [ROLE]: [],
      [STATUS]: [],
      [SCOPE]: [],
      [JOB_TITLE]: [],
      [TRACK]: [],
      [LEVEL]: [],
      [DIVISION]: [],
      [DEPARTMENT]: [],
      [TAGS]: [],
    },
    filteredValues: allUsers,
  },
  types: getObjectToNumberArray(types),
});

export const prepareAddUsersField = (
  classes,
  set,
  allAvailableUsers,
  blacklistedUsers,
  peopleFilters,
  hasTypeCheckbox = false
) => {
  return [
    {
      name: 'users',
      type: FIELD_TYPES.COLLAPSABLE_FILTERS,
      required: true,
      showSelection: true,
      parentOffset: hasTypeCheckbox ? 140 : 0,
      shouldRemoveFieldSpacing: true,
      shouldRemoveFieldError: true,
      shouldCalculateSelectionHeight: true,
      validators: [
        {
          type: 'required',
          validator: value => !isArrayEmpty(value?.filteredValues),
        },
      ],
      getResults: async selectedFilters => {
        const { data } = await http.get(API_USERS_BASIC, {
          params: {
            ...selectedFilters,
            ...PEOPLE_DEFAULT_ORDERING,
            [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
            [EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
            ...(set ? { [ABSENCE_SET]: set.id } : {}),
          },
          paramsSerializer: d => parseDuplicateParameters(d),
        });

        const allUsersIds = getObjectToNumberArray(allAvailableUsers);
        const availableUsers = data.filter(user =>
          allUsersIds.includes(user.id)
        );

        return prepareAddUserList(availableUsers, blacklistedUsers);
      },
      filters: peopleFilters,
    },
    ...(hasTypeCheckbox
      ? [
          {
            name: 'types',
            type: FIELD_TYPES.CHECKBOX_GROUP,
            hasScroll: true,
            checkboxContentClass: classes.types,
            fieldWrapperClass: classes.checkboxWrapperClass,
            getItems: () => set?.types,
          },
        ]
      : []),
  ];
};

export const filterUsers = users => {
  return users.reduce((acc, item) => {
    return item.isRemoved ? acc : [...acc, item.id];
  }, []);
};

export const prepareHolidayInitialData = (holiday = null) => ({
  name: holiday?.name || '',
  dates: holiday?.dates || [null],
  ...(holiday ? { id: holiday.id } : {}),
});

export const prepareManageHolidayFields = (
  classes,
  currentYear,
  setId,
  holidayId
) => [
  {
    name: 'name',
    type: FIELD_TYPES.TEXT,
    required: true,
    shouldValidateInitialValue: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
    ],
  },
  {
    name: 'dates',
    type: FIELD_TYPES.MULTIPLE_DATE_PICKER,
    translationKey: 'dates',
    fieldWrapperClass: classes.field,
    getMinDate: () => getStartOfYear(),
    getMaxDate: () => new Date(currentYear + 1, 11, 31),
    validators: [
      {
        validator: validateRequiredMultipleDates,
        type: 'required',
        isMultiple: true,
      },
      {
        type: 'uniqueDate',
        validator: values => {
          const validatedDates = validateUniqueMultipleDates(values);
          if (validatedDates) {
            return validatedDates;
          }

          const params = {
            ...(holidayId ? { holiday_id: holidayId } : {}),
            dates: values,
          };
          return http
            .get(api_existing_holiday_dates(setId), {
              params,
              paramsSerializer: data => parseDuplicateParameters(data),
            })
            .then(({ data }) => {
              return validateUniqueMultipleDates(values.concat(data));
            });
        },
        isMultiple: true,
      },
    ],
  },
];

export const prepareAbsenceSetInitialData = (set = null) => ({
  id: set?.id || null,
  [ABSENCE_SET_FIELD_NAMES.NAME]: set?.[ABSENCE_SET_FIELD_NAMES.NAME] || '',
  [ABSENCE_SET_FIELD_NAMES.TYPES]: set?.[ABSENCE_SET_FIELD_NAMES.TYPES]
    ? set?.[ABSENCE_SET_FIELD_NAMES.TYPES].map(absence => ({
        ...absence,
        [ROLLOVER_DATE]: absence[ROLLOVER_DATE]
          ? prepareRolloverDate(absence[ROLLOVER_DATE])
          : null,
      }))
    : [],
  user_count: set?.user_count || 0,
});

export const prepareManageAbsenceSetFields = isEdit => [
  {
    name: ABSENCE_SET_FIELD_NAMES.NAME,
    type: FIELD_TYPES.TEXT,
    required: true,
    shouldValidateInitialValue: !isEdit,
    hasDivider: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
      {
        type: 'availableName',
        validator: asyncDebounce(absenceSet => {
          const params = {
            name: trimString(absenceSet),
          };
          return http
            .get(API_UNIQUE_ABSENCE_SET, { params })
            .then(({ data }) => !data);
        }, 300),
        async: true,
      },
    ],
  },
  {
    name: ABSENCE_SET_FIELD_NAMES.TYPES,
    type: FIELD_TYPES.ABSENCES,
    shouldRemoveFieldSpacing: true,
    shouldRemoveFieldError: true,
    validators: [
      {
        type: 'absences',
        validator: value => validateMultipleField(value, ABSENCE_FIELDS),
        isMultiple: true,
      },
    ],
  },
];

export const prepareManageAbsenceInitialData = absence => {
  const [paidLeave] = ABSENCE_ICONS;

  return absence
    ? {
        ...absence,
        [ROLLOVER_DATE]: absence[ROLLOVER_DATE]
          ? prepareRolloverDate(absence[ROLLOVER_DATE])
          : null,
        [REASONS]: prepareReasons(absence[REASONS]),
      }
    : {
        [NAME]: '',
        [ICON]: paidLeave.id,
        [COLOR]: getRandomColorFromColorArray(ABSENCE_COLORS),
        [HAS_DAYS_ENABLED]: false,
        [NUMBER_OF_DAYS]: 0,
        [ACCRUAL]: null,
        [HAS_ROLLOVER]: false,
        [ROLLOVER_DATE]: null,
        [HAS_EXTENSIONS]: false,
        [EXTENSIONS]: [],
        [HAS_TEMPLATE]: false,
        [HAS_REASONS]: false,
        [REASONS]: [],
        [USER_COUNT]: 0,
      };
};

export const prepareManageAbsenceFields = classes => {
  const absenceFields = ABSENCE_FIELDS.map(aField => {
    if (aField.name === NAME) {
      return {
        ...aField,
        fieldWrapperClass: classes.name,
      };
    }
    if (aField.name === ICON) {
      return {
        ...aField,
        fieldWrapperClass: classes.icon,
      };
    }
    if (aField.name === COLOR) {
      return {
        ...aField,
        fieldWrapperClass: classes.color,
        className: classes.colorPicker,
      };
    }
    if (aField.name === HAS_DAYS_ENABLED) {
      return {
        ...aField,
        fieldWrapperClass: classes.hasAvailableDays,
      };
    }
    if (aField.name === NUMBER_OF_DAYS) {
      return {
        ...aField,
        fieldWrapperClass: classes.availableDays,
        customRootClass: classes.numberOfDaysField,
      };
    }
    if (aField.name === ACCRUAL) {
      return {
        ...aField,
        fieldWrapperClass: classes.accrual,
      };
    }
    if (aField.name === HAS_ROLLOVER) {
      return {
        ...aField,
        fieldWrapperClass: classes.hasRollover,
      };
    }
    if (aField.name === ROLLOVER_DATE) {
      return {
        ...aField,
        fieldWrapperClass: classes.rollover,
      };
    }
    if (aField.name === HAS_EXTENSIONS) {
      return {
        ...aField,
        fieldWrapperClass: classes.hasExtensions,
      };
    }
    if (aField.name === HAS_TEMPLATE) {
      return {
        ...aField,
        fieldWrapperClass: classes.hasTemplate,
      };
    }
    if (aField.name === HAS_REASONS) {
      return {
        ...aField,
        fieldWrapperClass: classes.hasReasons,
      };
    }
    return aField;
  });

  return [
    ...absenceFields,
    {
      name: ABSENCE_FIELD_NAMES.REASONS,
      type: FIELD_TYPES.CREATABLE_TEXTS,
      fieldWrapperClass: classes.reasons,
      isToggleable: true,
      shouldRemoveFieldSpacing: true,
      shouldRemoveFieldError: true,
      textField: ABSENCE_REASON_FIELD,
      shouldRender: values => values[ABSENCE_FIELD_NAMES.HAS_REASONS],
      validators: [
        {
          type: 'reasons',
          validator: value =>
            validateMultipleField(value, [ABSENCE_REASON_FIELD]),
          isMultiple: true,
        },
      ],
    },
  ];
};

export const prepareManageAbsenceTemplateInitialData = absence =>
  absence?.id
    ? {
        isEditTemplate: true,
        id: absence?.absenceId,
        [TEMPLATE]: absence[TEMPLATE] ?? '',
      }
    : {
        isEditTemplate: false,
        id: absence?.absenceId,
        [TEMPLATE]: '',
      };

const getAbsenceTemplateEditorFields = (classes, variableGroups) => [
  {
    name: 'templateDescription',
    type: FIELD_TYPES.SUBTITLE,
    variant: 'body2',
    fieldWrapperClass: classes.absenceTemplateDescription,
    shouldRemoveFieldSpacing: true,
  },
  {
    name: 'templateVariables',
    type: FIELD_TYPES.SUBTITLE,
    variant: 'body2',
    fieldWrapperClass: classes.absenceTemplateDescription,
    shouldRemoveFieldSpacing: true,
    label: (
      <span className={classes.templateVariablesGrid}>
        {variableGroups.map(group => (
          <span key={group.title} className={classes.templateVariablesColumn}>
            <span className={classes.templateVariablesTitle}>
              {group.title}
            </span>
            {group.values.map(value => (
              <span key={value} className={classes.templateVariablesValue}>
                {value}
              </span>
            ))}
          </span>
        ))}
      </span>
    ),
  },
  {
    name: TEMPLATE,
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    translationKey: 'template',
    syncA4PreviewPage: true,
    richTextModules: FULL_QUILL_MODULES,
    richTextFormats: FULL_QUILL_FORMATS,
    customRichTextEditorClass: classes.absenceTemplateRichText,
    shouldRemoveFieldSpacing: true,
    validators: [
      {
        validator: fieldValue => validateLength(fieldValue, 0, 10000),
        type: 'maxLength',
      },
    ],
  },
];

export const prepareManageAbsenceTemplateColumns = (
  classes,
  previewTitle,
  templateVariableGroups
) => [
  {
    id: 1,
    columnContentWrapperClass: classes.absenceTemplateEditorColumn,
    fields: [
      ...getAbsenceTemplateEditorFields(classes, templateVariableGroups),
    ],
  },
  {
    id: 2,
    title: previewTitle,
    titleVariant: 'subtitle1',
    columnTitleClass: classes.absenceTemplatePreviewTitle,
    columnClass: classes.absenceTemplatePreviewColumnInner,
    columnContentWrapperClass: classes.absenceTemplatePreviewColumn,
    fields: [
      {
        name: 'previewDescription',
        type: FIELD_TYPES.SUBTITLE,
        variant: 'body2',
        fieldWrapperClass: classes.absenceTemplatePreviewDescription,
        shouldRemoveFieldSpacing: true,
      },
      {
        name: 'a4_preview',
        type: FIELD_TYPES.A4_DOCUMENT_PREVIEW,
        previewKey: TEMPLATE,
        followEditorPage: true,
        fieldWrapperClass: classes.absenceTemplatePreviewField,
        shouldRemoveFieldSpacing: true,
      },
    ],
  },
];

export const prepareManageExtensionInitialData = extension =>
  extension || {
    name: '',
    description: '',
    number_of_days: 1,
  };

export const prepareManageExtensionFields = classes => [
  {
    name: 'name',
    type: FIELD_TYPES.TEXT,
    required: true,
    shouldValidateInitialValue: true,
    validators: [
      {
        type: 'required',
        validator: isEmpty,
      },
      {
        type: 'forbiddenCharacters',
        validator: forbiddenCharacters,
      },
      {
        type: 'nameMaxLength',
        validator: fieldValue => validateLength(fieldValue, 0, 255),
      },
    ],
  },
  {
    name: 'description',
    type: FIELD_TYPES.TEXT,
    multiline: true,
    rows: 5,
    validators: [
      {
        type: 'forbiddenCharacters',
        validator: forbiddenCharacters,
      },
      {
        type: 'descriptionMaxLength',
        validator: fieldValue => validateLength(fieldValue, 0, 255),
      },
    ],
  },
  {
    name: 'number_of_days',
    type: FIELD_TYPES.TEXT,
    inputType: 'number',
    translationKey: 'numberOfDays',
    customRootClass: classes.numberOfDaysField,
    shouldRemoveFieldSpacing: true,
    shouldRemoverFieldError: true,
    inputProps: {
      min: 1,
      max: 365,
    },
    required: true,
    validators: [
      { type: 'required', validator: isEmpty },
      {
        type: 'minMaxDays',
        validator: value => value >= 1,
      },
    ],
  },
];

export const prepareAssignedUsers = assignedUsers => {
  if (!assignedUsers) return [];

  return assignedUsers.map(user => ({
    ...user,
    isDisabled: user.has_absence_requests,
  }));
};
